/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `usersPagination` for the Caintra Connect frontend.
 * @module logic/users/usersPagination
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * Handles browse-mode pagination with cursor-based load more.
 * @param {Object} params
 * @returns {Promise<boolean>} True when a page change was applied
 */
export const handleBrowseUsersPageChange = async ({
  direction,
  currentPage,
  totalPages,
  lastKey,
  userId,
  navigate,
  fetchMoreUsersPage,
  setCurrentPage,
  setUsersData,
  setCompanies,
  setLastKey,
  setLimitReached,
  setIsLoading,
  onError,
}) => {
  if (direction === "next") {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
      return true;
    }

    if (!lastKey) {
      setLimitReached(true);
      return false;
    }

    try {
      setIsLoading(true);
      const next = await fetchMoreUsersPage(userId, navigate, lastKey);
      setUsersData((prev) => [...prev, ...(next.users ?? [])]);
      setCompanies((prev) => ({ ...prev, ...(next.companies ?? {}) }));
      setLastKey(next.lastEvaluatedKey ?? null);
      if (!next.lastEvaluatedKey) {
        setLimitReached(true);
      }
      setCurrentPage((prevPage) => prevPage + 1);
      return true;
    } catch (error) {
      onError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  if (direction === "prev" && currentPage > 1) {
    setCurrentPage((prevPage) => prevPage - 1);
    setLimitReached(false);
    return true;
  }

  return false;
};

/**
 * Handles search-mode pagination without remote load more.
 * @returns {boolean} True when a page change was applied
 */
export const handleSearchUsersPageChange = ({
  direction,
  currentPage,
  totalPages,
  setCurrentPage,
}) => {
  if (direction === "next" && currentPage < totalPages) {
    setCurrentPage((prevPage) => prevPage + 1);
    return true;
  }

  if (direction === "prev" && currentPage > 1) {
    setCurrentPage((prevPage) => prevPage - 1);
    return true;
  }

  return false;
};
